import axios from 'axios';
import { API_CONFIG } from '../config/authConfig';
import { AuthResponse } from '../types/authTypes';
import { clearUserSessionFully, getDisplayLogin, getSessionToken, isImpersonating, saveUserSession } from './sessionManager';

/**
 * Аккаунты этого устройства для экрана входа: живые сессии и аккаунты, из которых вышли кнопкой «Выйти».
 * У живой сессии хранится только пара { userId, sessionToken }: ФИО, аватар и роль приходят из validate-session,
 * поэтому устареть не могут, а истекшие и отозванные сессии отсеиваются при каждом открытии экрана входа.
 * У аккаунта, из которого вышли, токена нет: хранятся данные карточки «Вы вышли», снятые с сессии при выходе.
 */
const DEVICE_ACCOUNTS_KEY = 'zerde_device_accounts';
const MAX_DEVICE_ACCOUNTS = 10;

/** Данные карточки аккаунта */
export interface DeviceAccountProfile {
    username: string;
    fullName: string;
    avatar: string | null;
    badge: { bg: string; text: string } | null;
    targetInterface: string;
    pharmacyNum: string;
    roleLevel: number;
}

interface LiveDeviceAccount {
    userId: number;
    sessionToken: string;
}

interface SignedOutDeviceAccount {
    userId: number;
    profile: DeviceAccountProfile;
}

export type DeviceAccount = LiveDeviceAccount | SignedOutDeviceAccount;

/** Живая сессия: вход в аккаунт - клик по карточке, сессия сохраняется из userData */
export interface LiveAccountCard extends DeviceAccountProfile {
    userId: number;
    signedOut: false;
    sessionToken: string;
    userData: AuthResponse;
}

/** «Вы вышли»: клик открывает форму входа с логином этого аккаунта */
export interface SignedOutAccountCard extends DeviceAccountProfile {
    userId: number;
    signedOut: true;
}

export type DeviceAccountCard = LiveAccountCard | SignedOutAccountCard;

const isLive = (account: DeviceAccount): account is LiveDeviceAccount => 'sessionToken' in account;

const asText = (value: unknown): string => (typeof value === 'string' || typeof value === 'number' ? String(value) : '');

// Данные карточки из validate-session, из сохраненной сессии или из localStorage; без логина карточку не построить.
// У сотрудника точки на карточке и в форме входа - его собственный логин, а не логин точки
const parseProfile = (raw: any): DeviceAccountProfile | null => {
    const username = asText(raw?.staffLogin) || asText(raw?.username);
    if (!username) return null;
    const badge = raw.badge;
    return {
        username,
        fullName: asText(raw.fullName),
        avatar: asText(raw.avatar) || null,
        badge: badge && asText(badge.bg) && asText(badge.text) ? { bg: asText(badge.bg), text: asText(badge.text) } : null,
        targetInterface: asText(raw.targetInterface),
        pharmacyNum: asText(raw.pharmacyNum),
        roleLevel: Number(raw.roleLevel) || 0
    };
};

export const readDeviceAccounts = (): DeviceAccount[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(DEVICE_ACCOUNTS_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        const accounts: DeviceAccount[] = [];
        parsed.forEach((item) => {
            const userId = Number(item?.userId);
            if (!Number.isFinite(userId) || userId <= 0) return;
            if (typeof item.sessionToken === 'string' && item.sessionToken) {
                accounts.push({ userId, sessionToken: item.sessionToken });
                return;
            }
            const profile = parseProfile(item.profile);
            if (profile) accounts.push({ userId, profile });
        });
        return accounts;
    } catch {
        return [];
    }
};

const writeDeviceAccounts = (accounts: DeviceAccount[]): void => {
    const kept = accounts.slice(0, MAX_DEVICE_ACCOUNTS);
    try {
        if (kept.length === 0) {
            localStorage.removeItem(DEVICE_ACCOUNTS_KEY);
        } else {
            localStorage.setItem(DEVICE_ACCOUNTS_KEY, JSON.stringify(kept));
        }
    } catch {
        // Не хватило места: аватары - data URL до 300 КБ, карточки «Вы вышли» сохраняются без них
        try {
            const withoutAvatars = kept.map((item) => (isLive(item) ? item : { ...item, profile: { ...item.profile, avatar: null } }));
            localStorage.setItem(DEVICE_ACCOUNTS_KEY, JSON.stringify(withoutAvatars));
        } catch {
            // Хранилище недоступно: экран аккаунтов просто не запомнит список
        }
    }
};

/**
 * Добавляет аккаунты в начало списка устройства. У одной учетной записи остается одна запись:
 * новая сессия заменяет карточку «Вы вышли», а выход - живую сессию.
 */
export const rememberDeviceAccounts = (added: DeviceAccount[]): void => {
    const merged = [...added];
    for (const old of readDeviceAccounts()) {
        if (!merged.some((item) => item.userId === old.userId)) merged.push(old);
    }
    writeDeviceAccounts(merged);
};

/**
 * Сессия, с которой открыт портал, отозвана или истекла: она уходит из списка устройства.
 * Вместе с ней уходит карточка «Вы вышли» той же учетной записи: вход был после нее,
 * а истекшие и отозванные сессии карточками не остаются.
 */
export const forgetCurrentAccount = (): void => {
    const token = getSessionToken();
    if (!token) return;
    // Вход под сотрудником не трогает его карточку «Вы вышли»: она осталась от его собственного выхода
    const userId = isImpersonating() ? 0 : Number(localStorage.getItem('userId'));
    writeDeviceAccounts(readDeviceAccounts().filter((item) => (isLive(item) ? item.sessionToken !== token : item.userId !== userId)));
};

/**
 * Кандидаты на экран входа: список устройства и сессия, с которой открыт портал.
 * Вход под сотрудником (impersonation) в список не попадает и других аккаунтов не показывает.
 */
const collectCandidates = (): DeviceAccount[] => {
    const currentToken = getSessionToken();
    const currentUserId = Number(localStorage.getItem('userId'));
    const current: LiveDeviceAccount | null = currentToken && Number.isFinite(currentUserId) && currentUserId > 0
        ? { userId: currentUserId, sessionToken: currentToken }
        : null;

    if (current && isImpersonating()) return [current];

    const list = readDeviceAccounts();
    if (current && !list.some((item) => isLive(item) && item.sessionToken === current.sessionToken)) {
        // Та же учетная запись с другой сессией или карточкой «Вы вышли»: актуальнее сессия, с которой открыт портал
        return [current, ...list.filter((item) => item.userId !== current.userId)];
    }
    return list;
};

/**
 * Карточки экрана входа. Живые сессии проверяются на сервере, мертвые убираются из списка устройства.
 * Если сервер не ответил, сессия не считается мертвой: в списке остается, а в карточки не попадает.
 * Карточки «Вы вышли» строятся из сохраненных данных без запросов.
 */
export const loadDeviceAccountCards = async (): Promise<DeviceAccountCard[]> => {
    const candidates = collectCandidates();

    const checked = await Promise.all(candidates.map(async (candidate): Promise<{ candidate: DeviceAccount; card: DeviceAccountCard | null; dead: boolean }> => {
        if (!isLive(candidate)) {
            return { candidate, dead: false, card: { ...candidate.profile, userId: candidate.userId, signedOut: true } };
        }
        try {
            const response = await axios.post(`${API_CONFIG.AUTH_V2_URL}/validate-session`, { sessionToken: candidate.sessionToken });
            const user = response.data?.user;
            const profile = parseProfile(user);
            if (response.data?.valid && profile) {
                return {
                    candidate,
                    dead: false,
                    card: {
                        ...profile,
                        userId: Number(user.userId ?? candidate.userId),
                        signedOut: false,
                        sessionToken: candidate.sessionToken,
                        userData: { ...user, sessionToken: candidate.sessionToken }
                    }
                };
            }
            return { candidate, card: null, dead: true };
        } catch {
            return { candidate, card: null, dead: false };
        }
    }));

    // Список устройства пересобирается без мертвых сессий (в режиме «вход под сотрудником» он не меняется)
    if (!isImpersonating()) {
        writeDeviceAccounts(checked.filter((item) => !item.dead).map((item) => item.candidate));
    }
    // Сессия, с которой открыт портал, мертва: данные ее учетной записи в браузере больше не нужны
    const currentToken = getSessionToken();
    if (currentToken && checked.some((item) => item.dead && isLive(item.candidate) && item.candidate.sessionToken === currentToken)) {
        clearUserSessionFully();
    }

    const cards = checked.map((item) => item.card).filter((card): card is DeviceAccountCard => Boolean(card));
    // Старшая роль первой, остальные по логину: после выхода карточка остается на своем месте
    cards.sort((a, b) => (b.roleLevel - a.roleLevel) || a.username.localeCompare(b.username));
    return cards;
};

/**
 * Переключение на аккаунт устройства: данные другой учетной записи стираются, сохраняется выбранная.
 * Если сессия та же, с которой открыт портал, данные только обновляются (флаги профиля не сбрасываются).
 */
export const switchToDeviceAccount = (card: LiveAccountCard): void => {
    if (getSessionToken() !== card.sessionToken) clearUserSessionFully();
    saveUserSession(card.userData);
    if (card.avatar) localStorage.setItem('userAvatar', card.avatar);
};

// Отзыв сессии на сервере (с записью auth.logout в журнал)
const revokeSession = async (sessionToken: string | null): Promise<void> => {
    if (!sessionToken) return;
    try {
        await axios.post(`${API_CONFIG.AUTH_V2_URL}/logout`, { sessionToken });
    } catch {
        // Сервер недоступен: сессия закончится в 23:59, а на устройстве вход уже закрыт
    }
};

// Данные карточки «Вы вышли» снимаются с сохраненной сессии до ее очистки
const readCurrentProfile = (): DeviceAccountProfile | null => {
    let badge: unknown = null;
    try {
        badge = JSON.parse(localStorage.getItem('roleBadge') || 'null');
    } catch {
        badge = null;
    }
    return parseProfile({
        username: getDisplayLogin(),
        fullName: localStorage.getItem('fullName'),
        avatar: localStorage.getItem('userAvatar'),
        badge,
        targetInterface: localStorage.getItem('targetInterface'),
        pharmacyNum: localStorage.getItem('pharmacyNum'),
        roleLevel: localStorage.getItem('roleLevel')
    });
};

/**
 * Выход из текущей учетной записи: сессия отзывается на сервере, данные учетной записи стираются,
 * а на экране входа остается карточка «Вы вышли», чтобы вернуться в аккаунт, не вспоминая логин.
 * Остальные аккаунты устройства остаются живыми. Вход под сотрудником карточки не оставляет.
 */
export const logoutCurrentAccount = async (): Promise<void> => {
    const token = getSessionToken();
    const userId = Number(localStorage.getItem('userId'));
    const profile = readCurrentProfile();
    if (profile && Number.isFinite(userId) && userId > 0 && !isImpersonating()) {
        rememberDeviceAccounts([{ userId, profile }]);
    } else {
        forgetCurrentAccount();
    }
    clearUserSessionFully();
    await revokeSession(token);
};

/**
 * «Удалить с устройства»: карточка исчезает с экрана входа. Живая сессия при этом закрывается на сервере,
 * а если портал открыт с нее, стираются и данные учетной записи.
 */
export const removeDeviceAccount = async (card: DeviceAccountCard): Promise<void> => {
    writeDeviceAccounts(readDeviceAccounts().filter((item) => item.userId !== card.userId));
    if (card.signedOut) return;
    if (getSessionToken() === card.sessionToken) clearUserSessionFully();
    await revokeSession(card.sessionToken);
};
